import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const navLinks = [
  { path: '/', key: 'nav_home', match: '/' },
  { path: '/category/ri', key: 'nav_anime', match: 'category' },
  { path: '/category/m-boy', key: 'nav_manga', match: '' },
  { path: '/article/autumn-2026-preview', key: 'nav_article', match: 'article' },
  { path: '/about', key: 'nav_about', match: 'about' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/category/all?q=${encodeURIComponent(q)}`);
  };

  const isActive = (item: { path: string; match: string }) => {
    if (item.match === '/') return location.pathname === '/';
    if (item.match === 'article') return location.pathname.startsWith('/article');
    if (item.match === 'about') return location.pathname.startsWith('/about');
    if (item.path === '/category/ri') {
      return location.pathname.startsWith('/category') && !location.pathname.includes('/m-');
    }
    if (item.path === '/category/m-boy') {
      return location.pathname.startsWith('/category/m-');
    }
    return false;
  };

  const textColor = transparent ? 'text-background-50' : 'text-foreground-950';
  const borderColor = transparent ? 'border-background-50/20' : 'border-background-200/70';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        transparent ? 'bg-transparent' : 'bg-background-50/90 backdrop-blur-md shadow-sm'
      } border-b ${borderColor}`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <nav className="flex h-16 items-center justify-between" aria-label="主导航">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap"
            aria-label="动漫网 - 首页"
          >
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg ${
                transparent ? 'bg-background-50/20 text-background-50' : 'bg-primary-500 text-background-50'
              }`}
            >
              <i className="ri-tv-2-line" aria-hidden="true"></i>
            </span>
            <span className={`font-heading text-xl ${textColor}`}>动漫网</span>
          </Link>

          {/* Desktop nav */}
          <ul className="hidden items-center gap-1 md:flex">
            {navLinks.map((item) => (
              <li key={item.key}>
                <Link
                  to={item.path}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item)
                      ? transparent
                        ? 'bg-background-50/20 text-background-50'
                        : 'bg-primary-100 text-primary-700'
                      : `${textColor} hover:bg-background-100`
                  }`}
                >
                  {t(item.key)}
                </Link>
              </li>
            ))}
          </ul>

          {/* Search + actions */}
          <div className="hidden items-center gap-3 md:flex">
            <form onSubmit={onSearch} className="flex items-center">
              <div className="relative">
                <i
                  className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"
                  aria-hidden="true"
                ></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder={t('nav_search_placeholder')}
                  className={`h-9 w-56 rounded-md border bg-background-50 pl-9 pr-3 text-sm outline-none transition focus:border-primary-400 ${
                    transparent ? 'border-background-50/30' : 'border-background-200'
                  }`}
                  aria-label={t('nav_search_placeholder')}
                />
              </div>
              <button
                type="submit"
                className="ml-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600"
              >
                {t('nav_search')}
              </button>
            </form>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className={`flex h-9 w-9 items-center justify-center rounded-md md:hidden ${textColor}`}
            aria-label={t('nav_menu')}
            aria-expanded={mobileOpen}
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true"></i>
          </button>
        </nav>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="mb-3 rounded-lg border border-background-200 bg-background-50 p-3 md:hidden">
            <ul className="space-y-1">
              {navLinks.map((item) => (
                <li key={item.key}>
                  <Link
                    to={item.path}
                    className={`block rounded-md px-3 py-2 text-sm font-medium ${
                      isActive(item) ? 'bg-primary-100 text-primary-700' : 'text-foreground-950 hover:bg-background-100'
                    }`}
                  >
                    {t(item.key)}
                  </Link>
                </li>
              ))}
            </ul>
            <form onSubmit={onSearch} className="mt-3 flex items-center gap-2">
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={t('nav_search_placeholder')}
                className="h-9 flex-1 rounded-md border border-background-200 bg-background-50 px-3 text-sm outline-none focus:border-primary-400"
                aria-label={t('nav_search_placeholder')}
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50"
              >
                {t('nav_search')}
              </button>
            </form>
          </div>
        )}
      </div>
    </header>
  );
}